<script setup>
import { ref, computed, watch } from 'vue'
import { Head, Link, useForm } from '@inertiajs/vue3'
import {
    PhUser, PhBook, PhUploadSimple, PhCheckCircle,
    PhArrowRight, PhArrowLeft, PhLockSimple, PhCurrencyDollar,
    PhCalendar, PhMapPin, PhClock, PhXCircle, PhHourglass,
    PhGraduationCap, PhWarning,
} from '@phosphor-icons/vue'
import Layout    from '@pages/Shared/layouts/ApplicantLayout.vue'
import Input     from '@pages/Shared/form/Input.vue'
import Select    from '@pages/Shared/form/Select.vue'
import Textarea  from '@pages/Shared/form/Textarea.vue'
import Spinner   from '@pages/Shared/ui/Spinner.vue'
import { flashToast } from '@/toast.js'
import { NIGERIA_STATES } from '@/data/nigeria-states-lgas.js'

// ── Props ─────────────────────────────────────────────────────────────────
const props = defineProps({
    applicant:      Object,
    application:    Object,
    sections:       Array,
    session:        Object,
    hasPaidFormFee: Boolean,
})

// ── Steps ─────────────────────────────────────────────────────────────────
const STEPS = [
    { id: 1, label: 'Personal Info',   icon: PhUser         },
    { id: 2, label: 'Previous School', icon: PhBook         },
    { id: 3, label: 'Documents',       icon: PhUploadSimple },
    { id: 4, label: 'Review & Submit', icon: PhCheckCircle  },
]

const DOCUMENTS = [
    { label: 'Birth Certificate',       key: 'birth_certificate' },
    { label: 'Previous School Result',  key: 'previous_result'   },
    { label: 'Passport Photograph',     key: 'passport_photo'    },
]

const RELIGIONS = [
    { label: 'Islam',        value: 'Islam'        },
    { label: 'Christianity', value: 'Christianity' },
    { label: 'Traditional',  value: 'Traditional'  },
    { label: 'Others',       value: 'Others'       },
]

const GENDERS = [
    { label: 'Male',   value: 'male'   },
    { label: 'Female', value: 'female' },
]

// ── State ─────────────────────────────────────────────────────────────────
const currentStep  = ref(1)
const lgaOptions   = ref([])

// ── Derived status flags ──────────────────────────────────────────────────
const status    = computed(() => props.application?.status)
const interview = computed(() => props.application?.interview)

const isSubmitted = computed(() => status.value && status.value !== 'draft')

// Interview timeline states
const isInvited     = computed(() => status.value === 'invited')
const isInterviewed = computed(() => status.value === 'interviewed')
const isAdmitted    = computed(() => status.value === 'admitted')
const isRejected    = computed(() => status.value === 'rejected')
const isEnrolled    = computed(() => status.value === 'enrolled')

const interviewStatus = computed(() => interview.value?.status ?? null)

// ── Status display config ─────────────────────────────────────────────────
const applicationStatusConfig = computed(() => ({
    draft:       { color: 'bg-gray-100 text-gray-600',      label: 'Draft'       },
    submitted:   { color: 'bg-primary-50 text-primary-700',       label: 'Submitted'   },
    invited:     { color: 'bg-indigo-50 text-indigo-700',   label: 'Invited'     },
    interviewed: { color: 'bg-purple-50 text-purple-700',   label: 'Interviewed' },
    admitted:    { color: 'bg-green-50 text-green-700',     label: 'Admitted'    },
    rejected:    { color: 'bg-red-50 text-red-600',         label: 'Rejected'    },
    enrolled:    { color: 'bg-emerald-50 text-emerald-700', label: 'Enrolled'    },
}[status.value] ?? { color: 'bg-gray-100 text-gray-600', label: status.value }))

const interviewStatusConfig = computed(() => ({
    scheduled:   { color: 'bg-primary-50 text-primary-700',     dot: 'bg-primary-500',   label: 'Scheduled'   },
    rescheduled: { color: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500', label: 'Rescheduled' },
    completed:   { color: 'bg-green-50 text-green-700',   dot: 'bg-green-500',  label: 'Completed'   },
    absent:      { color: 'bg-red-50 text-red-600',       dot: 'bg-red-500',    label: 'Absent'      },
    skipped:     { color: 'bg-gray-100 text-gray-500',    dot: 'bg-gray-400',   label: 'Skipped'     },
}[interviewStatus.value] ?? null))

// ── Form ──────────────────────────────────────────────────────────────────
const form = useForm({
    section_id:             props.application?.section_id          ?? null,
    other_name:             props.applicant?.other_name            ?? '',
    dob:                    props.applicant?.dob                   ?? '',
    gender:                 props.applicant?.gender                ?? null,
    religion:               props.applicant?.religion              ?? '',
    nationality:            props.applicant?.nationality           ?? '',
    state_of_origin:        props.applicant?.state_of_origin       ?? '',
    lga_of_origin:          props.applicant?.lga_of_origin         ?? '',
    address:                props.applicant?.address               ?? '',
    phone:                  props.applicant?.phone                 ?? '',
    guardian_name:          props.applicant?.guardian_name         ?? '',
    guardian_phone:         props.applicant?.guardian_phone        ?? '',
    guardian_relationship:  props.applicant?.guardian_relationship ?? '',
    guardian_occupation:    props.applicant?.guardian_occupation   ?? '',
    guardian_address:       props.applicant?.guardian_address      ?? '',
    previous_school:        props.application?.previous_school     ?? '',
    previous_class:         props.application?.previous_class      ?? '',
    reason_for_leaving:     props.application?.reason_for_leaving  ?? '',
    _submit: false,
})

const sectionOptions = computed(() =>
    (props.sections ?? []).map(s => ({ label: s.name, value: s.id }))
)
const stateOptions = computed(() =>
    NIGERIA_STATES.map(s => ({ label: s.name, value: s.name }))
)

watch(() => form.state_of_origin, (state) => {
    const found = NIGERIA_STATES.find(s => s.name === state)
    lgaOptions.value = found?.lgas.map(l => ({ label: l, value: l })) || []
    form.lga_of_origin = ''
})

watch(() => form.nationality, (nationality) => {
    if (nationality !== 'Nigeria') {
        form.state_of_origin = ''
        form.lga_of_origin   = ''
        lgaOptions.value     = []
    }
})

// ── Navigation ────────────────────────────────────────────────────────────
const next = () => { if (currentStep.value < STEPS.length) currentStep.value++ }
const prev = () => { if (currentStep.value > 1) currentStep.value-- }

const saveDraft = () => {
    form._submit = false
    form.put('/applicant/application', {
        preserveScroll: true,
        onSuccess: (page) => flashToast(page.props.flash),
    })
}

const submitApplication = () => {
    form._submit = true
    form.post('/applicant/application/submit', {
        onSuccess: (page) => {
            flashToast(page.props.flash)
            currentStep.value = 1
        },
    })
}
</script>

<template>
    <Layout>
        <Head title="My Application" />

        <div class="mb-8">
            <h1 class="text-2xl font-bold text-gray-900">My Application</h1>
            <p class="text-gray-500 text-sm mt-1">{{ session?.name }} Academic Session</p>
        </div>

        <!-- ── Fee not paid ──────────────────────────────────────────────── -->
        <div v-if="!hasPaidFormFee"
            class="bg-white rounded-lg border border-gray-200  p-6 text-center">
            <div class="w-16 h-16 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-5">
                <PhLockSimple :size="28" class="text-amber-500" />
            </div>
            <h3 class="text-lg font-bold text-gray-800 mb-2">Application Form Locked</h3>
            <p class="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
                You must pay the <strong>Application Form Fee</strong> before you can fill and submit your application.
            </p>
            <Link href="/applicant/payments"
                class="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600
                       text-white font-semibold text-sm rounded-xl transition shadow">
                <PhCurrencyDollar :size="16" />
                Pay Now
            </Link>
        </div>

        <!-- ── Submitted — status tracker ───────────────────────────────── -->
        <template v-else-if="isSubmitted">
            <div class="space-y-4">

                <!-- Current status banner -->
                <div class="bg-white rounded-lg border border-gray-200  p-6">
                    <div class="flex items-start justify-between gap-4">
                        <div>
                            <p class="text-xs text-gray-400 mb-1">Application Status</p>
                            <span class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold capitalize"
                                :class="applicationStatusConfig.color">
                                {{ applicationStatusConfig.label }}
                            </span>
                        </div>
                        <div class="text-right text-sm">
                            <p class="text-xs text-gray-400">Session</p>
                            <p class="font-medium text-gray-700">{{ application.academic_session?.name }}</p>
                        </div>
                    </div>

                    <!-- Summary row -->
                    <div class="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div class="bg-gray-50 rounded-xl px-4 py-3">
                            <p class="text-xs text-gray-400 mb-0.5">Section Applied</p>
                            <p class="font-medium text-gray-800">{{ application.section?.name ?? '—' }}</p>
                        </div>
                        <div class="bg-gray-50 rounded-xl px-4 py-3">
                            <p class="text-xs text-gray-400 mb-0.5">Submitted</p>
                            <p class="font-medium text-gray-800">
                                {{ application.submitted_at
                                    ? new Date(application.submitted_at).toLocaleDateString()
                                    : '—' }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Interview card — shown once invited or beyond -->
                <div v-if="isInvited || isInterviewed || isAdmitted || isEnrolled"
                    class="bg-white rounded-lg border border-gray-200  p-6">
                    <h3 class="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <PhCalendar :size="16" class="text-indigo-500" />
                        Interview
                    </h3>

                    <div v-if="interview">
                        <!-- Interview status badge -->
                        <div v-if="interviewStatusConfig"
                            class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold capitalize mb-4"
                            :class="interviewStatusConfig.color">
                            <span class="w-1.5 h-1.5 rounded-full" :class="interviewStatusConfig.dot"></span>
                            {{ interviewStatusConfig.label }}
                        </div>

                        <div class="space-y-2 text-sm">
                            <!-- Scheduled date -->
                            <div v-if="interview.scheduled_at"
                                class="flex items-center gap-2 text-gray-600">
                                <PhClock :size="15" class="text-gray-400 shrink-0" />
                                <span>
                                    {{ new Date(interview.scheduled_at).toLocaleString(undefined, {
                                        dateStyle: 'medium', timeStyle: 'short'
                                    }) }}
                                </span>
                            </div>
                            <!-- Venue -->
                            <div v-if="interview.venue"
                                class="flex items-center gap-2 text-gray-600">
                                <PhMapPin :size="15" class="text-gray-400 shrink-0" />
                                <span>{{ interview.venue }}</span>
                            </div>
                            <!-- Invitation note -->
                            <div v-if="interview.invitation_note"
                                class="mt-3 p-3 bg-indigo-50 rounded-xl text-indigo-700 text-xs leading-relaxed">
                                {{ interview.invitation_note }}
                            </div>
                            <!-- Score (only after completion) -->
                            <div v-if="interview.score !== null && interviewStatus === 'completed'"
                                class="mt-3 flex items-center gap-2 p-3 bg-purple-50 rounded-xl">
                                <PhCheckCircle :size="16" class="text-purple-500 shrink-0" />
                                <span class="text-sm font-semibold text-purple-700">
                                    Interview Score: {{ interview.score }}/100
                                </span>
                            </div>
                            <!-- Absent notice -->
                            <div v-if="interviewStatus === 'absent'"
                                class="mt-3 flex items-center gap-2 p-3 bg-red-50 rounded-xl">
                                <PhWarning :size="16" class="text-red-500 shrink-0" />
                                <span class="text-sm text-red-700">
                                    You were marked absent for this interview. Please contact the school.
                                </span>
                            </div>
                        </div>
                    </div>

                    <p v-else class="text-sm text-gray-400">
                        Interview details will appear here once scheduled.
                    </p>
                </div>

                <!-- Admitted card -->
                <div v-if="isAdmitted || isEnrolled"
                    class="bg-green-50 border border-green-200 rounded-lg p-6">
                    <div class="flex items-center gap-3 mb-3">
                        <div class="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                            <PhGraduationCap :size="20" class="text-green-600" />
                        </div>
                        <div>
                            <p class="font-bold text-green-800">
                                {{ isEnrolled ? 'Enrolled' : 'Admission Granted' }}
                            </p>
                            <p class="text-xs text-green-600">
                                {{ isEnrolled
                                    ? 'You have been successfully enrolled.'
                                    : 'Congratulations! You have been offered admission.' }}
                            </p>
                        </div>
                    </div>
                    <Link v-if="isAdmitted" href="/applicant/payments"
                        class="inline-flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700
                               text-white text-sm font-semibold rounded-xl transition">
                        <PhCurrencyDollar :size="15" />
                        Pay Acceptance Fee
                    </Link>
                </div>

                <!-- Rejected card -->
                <div v-if="isRejected"
                    class="bg-red-50 border border-red-200 rounded-lg p-6">
                    <div class="flex items-start gap-3">
                        <div class="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center shrink-0">
                            <PhXCircle :size="20" class="text-red-600" />
                        </div>
                        <div>
                            <p class="font-bold text-red-800 mb-1">Application Not Successful</p>
                            <p class="text-sm text-red-600">
                                {{ application.rejection_reason
                                    ? application.rejection_reason
                                    : 'Your application was not successful at this time. Please contact the school for more information.' }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Under review notice (submitted, not yet invited) -->
                <div v-if="status === 'submitted'"
                    class="flex items-center gap-3 p-4 bg-primary-50 border border-primary-100 rounded-lg text-sm text-primary-700">
                    <PhHourglass :size="18" class="shrink-0" />
                    Your application is under review. You will be notified once a decision is made.
                </div>

            </div>
        </template>

        <!-- ── Draft form ────────────────────────────────────────────────── -->
        <template v-else>

            <!-- Step indicators -->
            <div class="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
                <template v-for="(step, i) in STEPS" :key="step.id">
                    <button
                        @click="currentStep = step.id"
                        class="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition shrink-0 border"
                        :class="currentStep === step.id
                            ? 'bg-primary text-white border-primary '
                            : step.id < currentStep
                                ? 'bg-primary/10 text-primary border-primary/20'
                                : 'bg-white text-gray-500 border-gray-200'">
                        <component :is="step.icon" :size="16" />
                        {{ step.label }}
                    </button>
                    <div v-if="i < STEPS.length - 1" class="w-6 h-px bg-gray-200 shrink-0" />
                </template>
            </div>

            <form @submit.prevent class="bg-white rounded-lg border border-gray-200 ">

                <!-- Step 1: Personal Info -->
                <div v-show="currentStep === 1" class="p-6 md:p-8">
                    <h3 class="text-base font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <PhUser :size="18" class="text-primary" /> Personal Information
                    </h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-5">
                            <div>
                                <p class="text-xs text-gray-400 mb-1">First Name</p>
                                <p class="text-sm font-semibold text-gray-800 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                    {{ applicant.first_name }}
                                </p>
                            </div>
                            <div>
                                <p class="text-xs text-gray-400 mb-1">Last Name</p>
                                <p class="text-sm font-semibold text-gray-800 px-3 py-2.5 rounded-lg bg-gray-50 border border-gray-100">
                                    {{ applicant.last_name }}
                                </p>
                            </div>
                            <Input label="Other Name" v-model="form.other_name" placeholder="Optional" />
                        </div>

                        <Select label="Applying for Section *" v-model="form.section_id"
                            :options="sectionOptions" placeholder="Select section"
                            :error="form.errors.section_id" />
                        <Input label="Date of Birth *" v-model="form.dob" type="date"
                            :error="form.errors.dob" />
                        <Select label="Gender *" v-model="form.gender" :options="GENDERS"
                            placeholder="Select gender" :error="form.errors.gender" />
                        <Select label="Religion *" v-model="form.religion" :options="RELIGIONS"
                            placeholder="Select religion" :error="form.errors.religion" />
                        <Select label="Nationality *" v-model="form.nationality"
                            :options="[{ label: 'Nigeria', value: 'Nigeria' }]"
                            placeholder="Select nationality" :error="form.errors.nationality" />
                        <Select label="State of Origin *" v-model="form.state_of_origin"
                            :options="stateOptions" placeholder="Select state"
                            :disabled="form.nationality !== 'Nigeria'"
                            :error="form.errors.state_of_origin" />
                        <Select label="LGA of Origin *" v-model="form.lga_of_origin"
                            :options="lgaOptions" placeholder="Select LGA"
                            :disabled="form.nationality !== 'Nigeria' || !form.state_of_origin"
                            :error="form.errors.lga_of_origin" />
                        <Input label="Phone Number *" v-model="form.phone" type="tel"
                            :error="form.errors.phone" />
                        <div class="md:col-span-2">
                            <Textarea label="Home Address" v-model="form.address"
                                placeholder="Full residential address" :rows="2" />
                        </div>

                        <div class="md:col-span-2">
                            <p class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4 mt-2">
                                Guardian / Parent Information
                            </p>
                        </div>
                        <Input label="Guardian Name *" v-model="form.guardian_name"
                            :error="form.errors.guardian_name" />
                        <Input label="Guardian Phone" v-model="form.guardian_phone" />
                        <Input label="Relationship" v-model="form.guardian_relationship"
                            placeholder="e.g. Father, Mother" />
                        <Input label="Occupation" v-model="form.guardian_occupation" />
                        <div class="md:col-span-2">
                            <Textarea label="Guardian Address" v-model="form.guardian_address" :rows="2" />
                        </div>
                    </div>
                </div>

                <!-- Step 2: Previous School -->
                <div v-show="currentStep === 2" class="p-6 md:p-8">
                    <h3 class="text-base font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <PhBook :size="18" class="text-primary" /> Previous School Information
                    </h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div class="md:col-span-2">
                            <Input label="Previous School Name" v-model="form.previous_school"
                                placeholder="Name of your last school" />
                        </div>
                        <div class="md:col-span-2">
                            <Input label="Class / Grade Completed" v-model="form.previous_class"
                                placeholder="e.g. JSS 3, Primary 6" />
                        </div>
                        <div class="md:col-span-2">
                            <Textarea label="Reason for Leaving" v-model="form.reason_for_leaving"
                                placeholder="Why are you leaving your previous school?" :rows="3" />
                        </div>
                    </div>
                </div>

                <!-- Step 3: Documents -->
                <div v-show="currentStep === 3" class="p-6 md:p-8">
                    <h3 class="text-base font-bold text-gray-800 mb-2 flex items-center gap-2">
                        <PhUploadSimple :size="18" class="text-primary" /> Supporting Documents
                    </h3>
                    <p class="text-sm text-gray-500 mb-6">
                        Upload the following documents. Accepted: PDF, JPG, PNG (max 2MB each).
                    </p>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div v-for="doc in DOCUMENTS" :key="doc.key">
                            <label class="block text-sm font-medium text-gray-700 mb-1.5">
                                {{ doc.label }}
                            </label>
                            <label class="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed
                                          border-gray-200 hover:border-primary/50 hover:bg-primary/5
                                          text-sm text-gray-500 hover:text-primary cursor-pointer transition w-full">
                                <PhUploadSimple :size="16" />
                                Click to upload
                                <input type="file" class="hidden" accept=".pdf,.jpg,.jpeg,.png"
                                    @change="(e) => form[doc.key] = e.target.files[0]" />
                            </label>
                            <p v-if="form.errors[doc.key]" class="text-xs text-red-500 mt-1">
                                {{ form.errors[doc.key] }}
                            </p>
                        </div>
                    </div>
                </div>

                <!-- Step 4: Review & Submit -->
                <div v-show="currentStep === 4" class="p-6 md:p-8">
                    <h3 class="text-base font-bold text-gray-800 mb-6 flex items-center gap-2">
                        <PhCheckCircle :size="18" class="text-primary" /> Review & Submit
                    </h3>
                    <div class="space-y-4">
                        <div class="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                            <div class="px-4 py-3 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Personal Details
                            </div>
                            <div class="divide-y divide-gray-100">
                                <div v-for="row in [
                                    { label: 'Full Name',     value: `${applicant.first_name} ${form.other_name} ${applicant.last_name}`.trim() },
                                    { label: 'Section',       value: sectionOptions.find(s => s.value === form.section_id)?.label ?? '—' },
                                    { label: 'Date of Birth', value: form.dob    || '—' },
                                    { label: 'Gender',        value: form.gender || '—' },
                                    { label: 'Phone',         value: form.phone  || '—' },
                                    { label: 'Guardian',      value: form.guardian_name || '—' },
                                ]" :key="row.label"
                                    class="flex items-center justify-between px-4 py-2.5 text-sm">
                                    <span class="text-gray-400">{{ row.label }}</span>
                                    <span class="font-medium text-gray-800 capitalize">{{ row.value }}</span>
                                </div>
                            </div>
                        </div>

                        <div class="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                            <div class="px-4 py-3 bg-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Previous School
                            </div>
                            <div class="divide-y divide-gray-100">
                                <div v-for="row in [
                                    { label: 'School', value: form.previous_school || 'Not provided' },
                                    { label: 'Class',  value: form.previous_class  || 'Not provided' },
                                ]" :key="row.label"
                                    class="flex items-center justify-between px-4 py-2.5 text-sm">
                                    <span class="text-gray-400">{{ row.label }}</span>
                                    <span class="font-medium text-gray-800">{{ row.value }}</span>
                                </div>
                            </div>
                        </div>

                        <!-- <div class="flex items-center gap-3 p-4 rounded-xl border bg-green-50 border-green-200">
                            <PhCheckCircle :size="20" class="text-green-600 shrink-0" />
                            <div class="text-sm">
                                <p class="font-medium text-green-800">Application Form Fee</p>
                                <p class="text-green-600">Paid ✓ — You are ready to submit</p>
                            </div>
                        </div> -->
                    </div>
                </div>

                <!-- Footer navigation -->
                <div class="px-6 md:px-8 py-5 border-t border-gray-100 flex items-center justify-between">
                    <button type="button" v-if="currentStep > 1" @click="prev"
                        class="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200
                               text-gray-600 hover:bg-gray-50 text-sm font-medium transition">
                        <PhArrowLeft :size="16" /> Previous
                    </button>
                    <div v-else />

                    <div class="flex items-center gap-3">
                        <button type="button" @click="saveDraft" :disabled="form.processing"
                            class="px-4 py-2.5 rounded-xl border border-gray-200 text-gray-600
                                   hover:bg-gray-50 text-sm font-medium transition flex items-center gap-2">
                            <Spinner v-if="form.processing && !form._submit" :size="14" />
                            Save Draft
                        </button>

                        <button v-if="currentStep < STEPS.length" type="button" @click="next"
                            class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary
                                   hover:bg-primary-light text-white text-sm font-semibold transition shadow">
                            Next <PhArrowRight :size="16" />
                        </button>

                        <button v-else type="button" @click="submitApplication"
                            :disabled="form.processing"
                            class="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-green-600
                                   hover:bg-green-700 text-white text-sm font-semibold transition shadow
                                   disabled:opacity-50">
                            <Spinner v-if="form.processing && form._submit" :size="14" />
                            Submit Application
                        </button>
                    </div>
                </div>
            </form>
        </template>
    </Layout>
</template>